import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Wallet, UserPlus, Pencil, Trash2, ChevronRight,
  CheckCircle, XCircle, Clock, AlertCircle,
} from 'lucide-react';
import { adminAPI, getErrorMessage } from '../../utils/api';
import { formatDate } from '../../utils/helpers';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminTableLoadingOverlay from '../../components/admin/AdminTableLoadingOverlay';
import { useDebounce } from '../../hooks/useDebounce';

const FETCH_LIMIT = 500;
const FIAT_SYMBOLS = new Set(['GBP', 'USD']);
const TOTAL_WALLET_SLOTS = 11;

const EMPTY_CREATE = {
  email: '',
  username: '',
  full_name: '',
  password: '',
  is_active: true,
  is_verified: true,
};

const EMPTY_EDIT = {
  email: '',
  username: '',
  full_name: '',
  phone_number: '',
  is_active: true,
  is_verified: true,
  password: '',
};

function formatBalanceChip(amount, symbol) {
  const n = Number(amount);
  if (FIAT_SYMBOLS.has(symbol)) {
    const prefix = symbol === 'GBP' ? '£' : '$';
    return `${symbol}: ${prefix}${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  const formatted = n % 1 === 0 ? n.toFixed(2) : parseFloat(n.toFixed(8)).toString();
  return `${symbol}: ${formatted}`;
}

function timeAgo(dateString) {
  if (!dateString) return null;
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const UserSetupChecklist = ({ user, cacheRef, onNavigate }) => {
  const cached = cacheRef.current[user.id];
  const [data, setData] = useState(cached || null);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    if (cached) return;
    Promise.all([
      adminAPI.getUserWallets(user.id),
      adminAPI.getUserBalances(user.id),
    ])
      .then(([walletsRes, balancesRes]) => {
        const result = {
          wallets: walletsRes.data.wallets || [],
          balances: balancesRes.data.balances || [],
        };
        cacheRef.current[user.id] = result;
        setData(result);
      })
      .catch(() => {
        const empty = { wallets: [], balances: [] };
        cacheRef.current[user.id] = empty;
        setData(empty);
      })
      .finally(() => setLoading(false));
  }, [user.id]);

  if (loading) {
    return (
      <tr className="bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700">
        <td colSpan={6} className="px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <div className="loading-spinner" style={{ width: 14, height: 14 }} />
            Loading setup status…
          </div>
        </td>
      </tr>
    );
  }

  const assignedWallets = (data?.wallets || []).filter((w) => w.wallet_address);
  const walletCount = assignedWallets.length;
  const hasWallets = walletCount > 0;

  const nonZeroBalances = (data?.balances || []).filter((b) => Number(b.available_balance) > 0);
  const hasBalance = nonZeroBalances.length > 0;
  const topBalance = nonZeroBalances[0];

  const kycStatus = user.kyc_status;
  const kycApproved = kycStatus === 'approved';
  const kycPending = kycStatus === 'pending';

  const completedSteps = (kycApproved ? 1 : 0) + (hasWallets ? 1 : 0) + (hasBalance ? 1 : 0);
  const progressPct = Math.round((completedSteps / 3) * 100);

  const lastActive = user.last_login ? timeAgo(user.last_login) : null;

  return (
    <tr>
      <td
        colSpan={6}
        className="p-0 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-700"
      >
        <div className="px-6 py-4">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            User Setup Status
          </p>

          {/* Checklist items */}
          <div className="flex items-center gap-6 flex-wrap">
            {/* KYC */}
            <div className="flex items-center gap-1.5">
              {kycApproved ? (
                <CheckCircle size={15} className="text-green-500 flex-shrink-0" />
              ) : kycPending ? (
                <Clock size={15} className="text-amber-500 flex-shrink-0" />
              ) : (
                <XCircle size={15} className="text-red-500 flex-shrink-0" />
              )}
              <span className={`text-sm font-medium ${kycApproved ? 'text-green-700 dark:text-green-400' : kycPending ? 'text-amber-700 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                {kycApproved ? 'KYC Approved' : kycPending ? 'KYC Pending' : 'KYC Required'}
              </span>
            </div>

            {/* Wallets */}
            <div className="flex items-center gap-1.5">
              {hasWallets ? (
                <CheckCircle size={15} className="text-green-500 flex-shrink-0" />
              ) : (
                <XCircle size={15} className="text-red-500 flex-shrink-0" />
              )}
              <span className={`text-sm font-medium ${hasWallets ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {hasWallets ? `Wallets Assigned (${walletCount}/${TOTAL_WALLET_SLOTS})` : 'No Wallets Assigned'}
              </span>
            </div>

            {/* Balance */}
            <div className="flex items-center gap-1.5">
              {hasBalance ? (
                <CheckCircle size={15} className="text-green-500 flex-shrink-0" />
              ) : (
                <AlertCircle size={15} className="text-gray-400 flex-shrink-0" />
              )}
              <span className={`text-sm font-medium ${hasBalance ? 'text-green-700 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                {hasBalance
                  ? `Funded — ${formatBalanceChip(topBalance.available_balance, topBalance.asset_symbol)}`
                  : 'No Balance'}
              </span>
            </div>

            {/* Last active */}
            <div className="flex items-center gap-1.5">
              <Clock size={15} className="text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {lastActive ? `Active ${lastActive}` : 'Never logged in'}
              </span>
            </div>
          </div>

          {/* Quick action buttons */}
          <div className="flex items-center gap-3 mt-4 pt-3 border-t border-gray-200 dark:border-gray-600">
            <button
              onClick={() => onNavigate(`/admin/kyc?user=${user.id}`)}
              className="text-xs px-3 py-1.5 rounded-lg border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              Review KYC
            </button>
            <button
              onClick={() => onNavigate(`/admin/users/${user.id}/wallets`)}
              className="text-xs px-3 py-1.5 rounded-lg border border-teal-200 dark:border-teal-800 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
            >
              Manage Wallets & Balances
            </button>
            <button
              onClick={() => onNavigate(`/admin/trades?user=${user.id}`)}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              View Trades
            </button>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
              <span>Setup Progress</span>
              <span>{completedSteps}/3 steps complete</span>
            </div>
            <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
              <div
                className="bg-green-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

const AdminUsers = () => {
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.role === 'super_admin';

  const [rawUsers, setRawUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const debouncedSearch = useDebounce(search, 300);

  const [expandedUserId, setExpandedUserId] = useState(null);
  const checklistCache = useRef({});

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [createLoading, setCreateLoading] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [editLoading, setEditLoading] = useState(false);

  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [debouncedSearch]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getUsers({
        search: debouncedSearch || undefined,
        skip: 0,
        limit: FETCH_LIMIT,
      });
      setRawUsers(response.data.users);
      setPage(1);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = statusFilter
    ? rawUsers.filter((u) => (statusFilter === 'active' ? u.is_active : !u.is_active))
    : rawUsers;
  const total = filteredUsers.length;
  const users = filteredUsers.slice((page - 1) * pageSize, page * pageSize);

  const handleAction = async (userId, action) => {
    try {
      if (action === 'suspend') {
        await adminAPI.suspendUser(userId);
        toast.success('User suspended');
      } else {
        await adminAPI.activateUser(userId);
        toast.success('User activated');
      }
      fetchUsers();
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const toggleExpand = (userId) => {
    setExpandedUserId((prev) => (prev === userId ? null : userId));
  };

  // --- Create ---
  const openCreateModal = () => {
    setCreateForm(EMPTY_CREATE);
    setShowCreateModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.username || !createForm.password) {
      toast.error('Email, username, and password are required');
      return;
    }
    if (createForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setCreateLoading(true);
    try {
      await adminAPI.createUser(createForm);
      toast.success('User created');
      setShowCreateModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCreateLoading(false);
    }
  };

  // --- Edit ---
  const openEditModal = (user) => {
    setEditingUser(user);
    setEditForm({
      email: user.email || '',
      username: user.username || '',
      full_name: user.full_name || '',
      phone_number: user.phone || '',
      is_active: user.is_active ?? true,
      is_verified: user.email_verified ?? false,
      password: '',
    });
    setShowEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setEditLoading(true);
    try {
      const body = {
        email: editForm.email || undefined,
        username: editForm.username || undefined,
        full_name: editForm.full_name || undefined,
        phone_number: editForm.phone_number || undefined,
        is_active: editForm.is_active,
        is_verified: editForm.is_verified,
      };
      if (editForm.password) body.password = editForm.password;
      await adminAPI.updateUser(editingUser.id, body);
      toast.success('User updated');
      setShowEditModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setEditLoading(false);
    }
  };

  // --- Delete ---
  const handleDelete = async (user) => {
    if (!window.confirm(`Deactivate ${user.email}? This will soft-delete the account.`)) return;
    setDeletingId(user.id);
    try {
      await adminAPI.deleteUser(user.id);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  };

  const filterOptions = [
    { value: 'active', label: 'Active' },
    { value: 'suspended', label: 'Suspended' },
  ];

  if (loading && users.length === 0)
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner" />
      </div>
    );

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="admin-users">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-4xl font-bold text-foreground">User Management</h1>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <UserPlus size={16} />
          Create User
        </button>
      </div>

      <AdminTableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name or email..."
        filterValue={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); setPage(1); }}
        filterOptions={filterOptions}
        filterPlaceholder="All statuses"
        total={total}
        loading={loading}
      />

      <Card className="glass-card p-6 overflow-hidden">
        <AdminTableLoadingOverlay loading={loading}>
          <div className="admin-table-wrapper">
            <table>
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-muted-foreground text-sm font-medium pb-3">Name</th>
                  <th className="text-left text-muted-foreground text-sm font-medium pb-3">Email</th>
                  <th className="text-left text-muted-foreground text-sm font-medium pb-3">Role</th>
                  <th className="text-left text-muted-foreground text-sm font-medium pb-3">Status</th>
                  <th className="text-left text-muted-foreground text-sm font-medium pb-3">Joined</th>
                  <th className="text-left text-muted-foreground text-sm font-medium pb-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <React.Fragment key={user.id}>
                    <tr
                      className="table-row cursor-pointer"
                      data-testid={`user-row-${user.id}`}
                      onClick={() => toggleExpand(user.id)}
                    >
                      <td className="py-4 text-foreground">{user.full_name}</td>
                      <td className="py-4 text-foreground">{user.email}</td>
                      <td className="py-4 text-foreground capitalize">{user.role}</td>
                      <td className="py-4">
                        <span className={`badge ${user.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {user.is_active ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td className="py-4 text-muted-foreground text-sm">{formatDate(user.created_at)}</td>
                      <td className="py-4">
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => navigate(`/admin/users/${user.id}/wallets`)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 border border-teal-200 dark:border-teal-800 rounded-lg hover:bg-teal-100 dark:hover:bg-teal-900/30 transition-colors"
                            title="Assign wallet addresses"
                          >
                            <Wallet size={12} />
                            Wallets
                          </button>
                          <button
                            onClick={() => openEditModal(user)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            title="Edit user"
                          >
                            <Pencil size={12} />
                            Edit
                          </button>
                          <Button
                            size="sm"
                            onClick={() => handleAction(user.id, user.is_active ? 'suspend' : 'activate')}
                            variant={user.is_active ? 'destructive' : 'default'}
                            data-testid={`action-btn-${user.id}`}
                          >
                            {user.is_active ? 'Suspend' : 'Activate'}
                          </Button>
                          {isSuperAdmin && (
                            <button
                              onClick={() => handleDelete(user)}
                              disabled={deletingId === user.id}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                              title="Delete user"
                            >
                              <Trash2 size={12} />
                              {deletingId === user.id ? '...' : 'Delete'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedUserId === user.id && (
                      <UserSetupChecklist
                        user={user}
                        cacheRef={checklistCache}
                        onNavigate={navigate}
                      />
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <AdminPagination
            page={page}
            pageSize={pageSize}
            onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
            total={total}
            onPageChange={setPage}
            loading={loading}
          />
        </AdminTableLoadingOverlay>
      </Card>

      {/* Create User Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Username <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={createForm.username}
                onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="johndoe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Full Name</label>
              <input
                type="text"
                value={createForm.full_name}
                onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Min. 8 characters"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Account Active</label>
              <button
                type="button"
                onClick={() => setCreateForm({ ...createForm, is_active: !createForm.is_active })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${createForm.is_active ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${createForm.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Email Verified</label>
              <button
                type="button"
                onClick={() => setCreateForm({ ...createForm, is_verified: !createForm.is_verified })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${createForm.is_verified ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${createForm.is_verified ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createLoading}>
                {createLoading ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Email</label>
              <input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Username</label>
              <input
                type="text"
                value={editForm.username}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Full Name</label>
              <input
                type="text"
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Phone Number</label>
              <input
                type="text"
                value={editForm.phone_number}
                onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="+1 555 000 0000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Password</label>
              <input
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Leave blank to keep current password"
              />
              <p className="text-xs text-muted-foreground mt-1">Leave blank to keep current password</p>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Account Active</label>
              <button
                type="button"
                onClick={() => setEditForm({ ...editForm, is_active: !editForm.is_active })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editForm.is_active ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editForm.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Email Verified</label>
              <button
                type="button"
                onClick={() => setEditForm({ ...editForm, is_verified: !editForm.is_verified })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editForm.is_verified ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editForm.is_verified ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={editLoading}>
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
